import { useEffect, useLayoutEffect } from 'react';

import { useLocation } from '@tanstack/react-router';

/** Matches `App.SideBar.Root` in `BaseLayout` — used to scope sidebar nav links only. */
export const SITE_CONTROL_SIDEBAR_DATA_ATTR = 'data-sitecontrol-sidebar';

/** Matches `App.TopBar.SideBarToggle` — used to dock the overlay sidebar from the site picker. */
export const SITE_CONTROL_SIDEBAR_TOGGLE_ATTR = 'data-sitecontrol-sidebar-toggle';

const sidebarRootSelector = `aside[${SITE_CONTROL_SIDEBAR_DATA_ATTR}]`;

const applySidebarNavTabIndex = () => {
  const aside = document.querySelector<HTMLElement>(sidebarRootSelector);
  if (!aside) return;

  const hideFromFocusOrder = aside.getAttribute('aria-hidden') === 'true';
  const tabIndex = hideFromFocusOrder ? -1 : 0;

  aside.querySelectorAll<HTMLElement>('a[href], button').forEach(el => {
    el.tabIndex = tabIndex;
  });
};

/**
 * Scale UI's sidebar item links always use `tabIndex={0}` in the DOM, and the inline site picker is
 * a button. When the drawer is closed, the shell sets `aria-hidden` on the `<aside>` but those
 * controls remain in the tab order. Sync `tabIndex` to `-1` while hidden, and re-apply after React
 * commits (which resets `tabIndex`).
 *
 * Important: only observe `aria-hidden` on the aside (never `tabindex` on descendants), otherwise
 * applying `tabIndex` would refire the MutationObserver indefinitely and freeze the page.
 */
export const useSidebarNavTabIndexWhenHidden = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    void pathname;
    applySidebarNavTabIndex();
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    let sidebarMutationObserver: MutationObserver | null = null;

    const attachSidebarMutationObserver = () => {
      sidebarMutationObserver?.disconnect();

      const aside = document.querySelector<HTMLElement>(sidebarRootSelector);
      if (!aside || cancelled) return;

      applySidebarNavTabIndex();

      sidebarMutationObserver = new MutationObserver(() => {
        applySidebarNavTabIndex();
      });

      sidebarMutationObserver.observe(aside, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
        childList: true,
        subtree: true,
      });
    };

    attachSidebarMutationObserver();

    const onFullscreenChangeReattachObserver = () => attachSidebarMutationObserver();

    document.addEventListener('fullscreenchange', onFullscreenChangeReattachObserver);

    return () => {
      cancelled = true;
      sidebarMutationObserver?.disconnect();
      document.removeEventListener('fullscreenchange', onFullscreenChangeReattachObserver);
    };
  }, []);
};
